import { useLoadLookup } from 'components/common/FilterArea/Filters/useLoadLookup';
import { CodeNameModel } from 'components/common/types';
import { InputShape, InputSize, InputTheme } from 'components/common/types/Input.types';
import { Store } from 'components/common/types/Store.types';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import AsyncSelect from 'lib/AsyncSelect';
import { ShadowStyle } from 'consts/shadow';
import { Padding } from 'lib/BaseSelect/BaseSelect.types';
import { Label } from 'lib/Label';
import { useDispatch, useSelector } from 'react-redux';
import { changeDealData } from 'store/dealManagement/reducer';
import { getIsReseller } from 'store/publisher/selectors';
import { isReadOnly } from 'utils/isReadOnly';
import { isRequiredDirectSalesField } from 'utils/isRequiredDirectSalesField';
import { getIsEditingDisabled } from 'store/dealManagement/selectors/getIsEditingDisabled';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import usePendoTracking from 'customHooks/usePendoTracking';

interface SelectSalesTeamProps {
  isDisabled?: boolean;
}

const SelectSalesTeam = ({ isDisabled }: SelectSalesTeamProps): React.ReactElement | null => {
  const dispatch = useDispatch();

  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);

  const salesTeam = useSelector((state: Store) => state.dealManagement.commonDeal.salesTeam);
  const isEditingDisabled = useSelector(getIsEditingDisabled);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const directSalesRequiredFieldsConfig = useSelector((state: Store) => state.publisher.directSalesRequiredFields);
  const isRequired = isRequiredDirectSalesField({
    fieldsConfig: directSalesRequiredFieldsConfig,
    fieldName: 'salesPerson',
  });
  const isReseller = useSelector(getIsReseller);

  const { onLoadSalesTeam } = useLoadLookup();

  const { isCampaignReadOnly } = useIsReadOnly();
  const { getPendoDataId } = usePendoTracking();

  const handleSelectSalesTeam: SelectHandler<CodeNameModel> = (dropdownName, value): void => {
    dispatch(changeDealData({ [dropdownName]: value }));
  };

  if (!isDirectSalesCampaignType && isReseller) return null;

  return (
    <>
      <Label id="salesTeam" theme={InputTheme.FLAT_GRAY} label="Sales team" isRequired={isRequired} />
      <Tooltip tooltip={salesTeam?.name} direction={TooltipDirection.BOTTOM} isHiddenOnFocus>
        <AsyncSelect
          name="salesTeam"
          dataTestId="async-select-salesTeam"
          placeholder=""
          theme={InputTheme.WHITE_WITH_BORDER}
          size={InputSize.EXTRA_SMALL}
          padding={Padding.NONE}
          shadow={ShadowStyle.NONE}
          shape={InputShape.ROUNDED}
          selectedValue={salesTeam}
          loadData={onLoadSalesTeam}
          onSelect={handleSelectSalesTeam}
          isDisabled={isDisabled || isReadOnly(bookingStatusCode, isEditingDisabled) || isCampaignReadOnly}
          dataPendoId={getPendoDataId('Sales team')}
        />
      </Tooltip>
    </>
  );
};

export default SelectSalesTeam;
